import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { addDays } from 'date-fns';
import * as React from 'react';
import Button from 'src/components/molecules/Button';
import LogoutButton from 'src/components/molecules/LogoutButton';
import useScreenSize from 'src/hooks/useScreenSize';
import { formatDateLocale } from 'src/lib/date';
import { isRoomAvailableOn, openOfficesOn } from 'src/lib/offices';
import { useCalendarPageContext } from '../../CalendarPageContext';
import DayTable from './DayTable';
import OFFICE_LAYOUTS from './layouts';

const HIDDEN_TABLES = new Set([13]);

interface IProps {
  date: Date;
  onDateSelect?: (date: Date) => void;
}

const DayDetail: React.FC<IProps> = ({ date, onDateSelect }) => {
  const { isMobile } = useScreenSize();
  const { users, bookings: bookingsByDate, offices, rooms: tablesData, showWeekends } = useCalendarPageContext();
  const visibleTables =
    tablesData?.filter(table => !HIDDEN_TABLES.has(table.id) && isRoomAvailableOn(table, offices, date)) ?? [];
  const officeLayouts = openOfficesOn(offices, date).flatMap(office => {
    const Layout = OFFICE_LAYOUTS[office.id];
    return Layout ? [{ office, Layout }] : [];
  });
  const standingTableIds = React.useMemo(
    () => new Set(tablesData?.filter(table => table.standing).map(table => table.id)),
    [tablesData]
  );
  const bookings = bookingsByDate?.[date.toISOString()] ?? [];

  const [activeTableId, setActiveTableId] = React.useState<number | null>(null);

  const bookingsByTableId = React.useMemo(() => {
    if (!bookings) {
      return {};
    }
    const bookingsByTableId: Record<number, typeof bookings> = {};
    for (const booking of bookings) {
      const tableId = booking.room_id[0];
      if (!bookingsByTableId[tableId]) {
        bookingsByTableId[tableId] = [];
      }
      bookingsByTableId[tableId].push(booking);
    }
    return bookingsByTableId;
  }, [bookings]);

  const filledTables = React.useMemo(() => {
    return Object.fromEntries(
      Object.keys(bookingsByTableId).map(id => {
        const tableId = Number(id);
        const booking = bookingsByTableId[tableId][0];
        const bookingUser = users?.find(u => u.id === booking.organizer_id[0]) ?? null;
        return [tableId, { id: tableId, user: bookingUser }];
      })
    );
  }, [bookingsByTableId, users]);

  const handleOffsetDate = (offset: number) => {
    if (!onDateSelect) return;
    let newDate = addDays(date, offset);
    // Account for weekends if they are hidden
    if (!isMobile && !showWeekends) {
      const dayOfWeek = newDate.getDay();
      if (dayOfWeek === 0) {
        newDate = addDays(newDate, -2); // Sunday, skip to Friday
      } else if (dayOfWeek === 6) {
        newDate = addDays(newDate, +2); // Saturday, skip to Monday
      }
    }
    onDateSelect(newDate);
  };

  return (
    <div className="day-detail">
      <div className="day-detail__nav">
        <LogoutButton />
        <Button onClick={() => handleOffsetDate(-1)}>
          <ArrowLeftIcon />
        </Button>
        <span className="panel-title-text">{formatDateLocale(date)}</span>
        <Button onClick={() => handleOffsetDate(1)}>
          <ArrowRightIcon />
        </Button>
      </div>

      {officeLayouts.map(({ office, Layout }) => (
        <div key={office.id} className="day-detail__room">
          <Layout
            activeTableId={activeTableId}
            filledTables={filledTables}
            standingTableIds={standingTableIds}
            onTableClick={tableId => setActiveTableId(tableId)}
          />
          <span className="day-detail__room-label">{office.name}</span>
        </div>
      ))}
      <div className="day-detail__tables">
        {visibleTables.map(table => (
          <DayTable
            key={table.id}
            table={table}
            bookings={bookingsByTableId[table.id] ?? []}
            onSelect={() => setActiveTableId(table.id)}
            isActive={table.id === activeTableId}
          />
        ))}
      </div>
    </div>
  );
};

export default DayDetail;
